import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AiPromptTemplateType,
  AiPromptUseCase,
  AI_EXTERNAL_CHANNEL_KEYS,
  buildExternalUnmutedUseCaseId,
  type AiExternalChannelKey,
  type AiKernelFunctionSetting,
  type FollowUpAutomation,
  type UpsertFollowUpAutomationRequest,
} from '../types/AiPromptConfig';
import {
  createFollowUpAutomation,
  deleteFollowUpAutomation,
  getAiKernelFunctionPolicy,
  getAiPromptConfig,
  listFollowUpAutomations,
  updateFollowUpAutomation,
} from '../services/aiPromptConfigService';
import styles from './FollowUpAutomationsPanel.module.css';

type SurfaceOption = {
  id: string;
  labelKey: string;
};

const SURFACES: SurfaceOption[] = [
  { id: AiPromptUseCase.ReplyDefault, labelKey: 'surfaceEmployee' },
  { id: AiPromptUseCase.ReplyExternalUnmuted, labelKey: 'surfaceCustomers' },
  ...AI_EXTERNAL_CHANNEL_KEYS.map((channel) => ({
    id: buildExternalUnmutedUseCaseId(channel),
    labelKey: channelLabelKey(channel),
  })),
  { id: AiPromptUseCase.ReplyInstagramComment, labelKey: 'surfaceInstagramComment' },
  { id: AiPromptUseCase.ReplyMessengerComment, labelKey: 'surfaceMessengerComment' },
  { id: AiPromptUseCase.ReplyWhatsAppGroup, labelKey: 'surfaceWhatsAppGroup' },
];

const CHAT_SURFACE_IDS = new Set<string>([
  AiPromptUseCase.ReplyDefault,
  AiPromptUseCase.ReplyExternalUnmuted,
]);

const THREAD_SURFACE_IDS = new Set<string>([
  AiPromptUseCase.ReplyInstagramComment,
  AiPromptUseCase.ReplyMessengerComment,
  AiPromptUseCase.ReplyWhatsAppGroup,
]);

const SURFACE_GROUPS: Array<{ titleKey: string; items: SurfaceOption[] }> = [
  { titleKey: 'surfaceGroupChats', items: SURFACES.filter((item) => CHAT_SURFACE_IDS.has(item.id)) },
  { titleKey: 'surfaceGroupChannels', items: SURFACES.filter((item) => !CHAT_SURFACE_IDS.has(item.id) && !THREAD_SURFACE_IDS.has(item.id)) },
  { titleKey: 'surfaceGroupThreads', items: SURFACES.filter((item) => THREAD_SURFACE_IDS.has(item.id)) },
];

function channelLabelKey(channel: AiExternalChannelKey): string {
  switch (channel) {
    case 'whatsapp': return 'surfaceWhatsapp';
    case 'telegram': return 'surfaceTelegram';
    case 'instagram': return 'surfaceInstagram';
    case 'messenger': return 'surfaceMessenger';
    case 'webchat': return 'surfaceWebchat';
    case 'tiktok': return 'surfaceTiktok';
    default: return 'surfaceCustomers';
  }
}

type Draft = {
  id: string | null;
  name: string;
  isEnabled: boolean;
  assignedUseCaseIds: string[];
  delayMinutes: string;
  systemPrompt: string;
  limitTools: boolean;
  allowedFunctionIds: string[];
};

const emptyDraft = (): Draft => ({
  id: null,
  name: '',
  isEnabled: true,
  assignedUseCaseIds: [AiPromptUseCase.ReplyExternalUnmuted],
  delayMinutes: '',
  systemPrompt: '',
  limitTools: false,
  allowedFunctionIds: [],
});

const draftFrom = (automation: FollowUpAutomation): Draft => ({
  id: automation.id,
  name: automation.name,
  isEnabled: automation.isEnabled,
  assignedUseCaseIds: [...automation.assignedUseCaseIds],
  delayMinutes: automation.delayMinutes == null ? '' : String(automation.delayMinutes),
  systemPrompt: automation.systemPrompt,
  limitTools: automation.allowedFunctionIds.length > 0,
  allowedFunctionIds: [...automation.allowedFunctionIds],
});

const sortOperations = (rows: FollowUpAutomation[]): FollowUpAutomation[] =>
  [...rows].sort((left, right) => left.name.localeCompare(right.name));

type FollowUpAutomationsPanelProps = {
  canManage: boolean;
};

type ChoiceProps = {
  checked: boolean;
  disabled: boolean;
  label: string;
  tone: 'channel' | 'tool';
  onChange: () => void;
};

const Choice: React.FC<ChoiceProps> = ({ checked, disabled, label, tone, onChange }) => (
  <label className={`${styles.choice} ${tone === 'tool' ? styles.choiceTool : styles.choiceChannel} ${checked ? styles.choiceOn : ''}`}>
    <input
      type="checkbox"
      className={styles.choiceInput}
      checked={checked}
      disabled={disabled}
      onChange={onChange}
    />
    <span className={styles.choiceMark} aria-hidden="true" />
    <span className={styles.choiceLabel}>{label}</span>
  </label>
);

export const FollowUpAutomationsPanel: React.FC<FollowUpAutomationsPanelProps> = ({ canManage }) => {
  const { t } = useTranslation('admin');
  const key = (name: string) => t(`aiSettings.useCases.followUp.${name}`);
  const [operations, setOperations] = useState<FollowUpAutomation[]>([]);
  const [tools, setTools] = useState<AiKernelFunctionSetting[]>([]);
  const [starterPrompt, setStarterPrompt] = useState('');
  const [starterReady, setStarterReady] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [messageTone, setMessageTone] = useState<'success' | 'error'>('success');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rowsResult, policyResult, promptResult] = await Promise.allSettled([
        listFollowUpAutomations(),
        getAiKernelFunctionPolicy(AiPromptUseCase.FollowUp),
        getAiPromptConfig(AiPromptTemplateType.FollowUp),
      ]);
      if (rowsResult.status === 'fulfilled') {
        setOperations(sortOperations(rowsResult.value));
      } else {
        setMessageTone('error');
        setMessage(t('aiSettings.useCases.followUp.loadFailed'));
      }
      if (policyResult.status === 'fulfilled') {
        setTools(policyResult.value.functions.filter((item) => item.enabled));
      }
      setStarterPrompt(promptResult.status === 'fulfilled' ? promptResult.value.systemPrompt ?? '' : '');
      setStarterReady(true);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const enabledTools = useMemo(() => tools, [tools]);

  const toggleSurface = (id: string) => {
    setDraft((current) => {
      if (!current) return current;
      const selected = current.assignedUseCaseIds.includes(id)
        ? current.assignedUseCaseIds.filter((item) => item !== id)
        : [...current.assignedUseCaseIds, id];
      return { ...current, assignedUseCaseIds: selected };
    });
  };

  const toggleTool = (id: string) => {
    setDraft((current) => {
      if (!current) return current;
      const selected = current.allowedFunctionIds.includes(id)
        ? current.allowedFunctionIds.filter((item) => item !== id)
        : [...current.allowedFunctionIds, id];
      return { ...current, allowedFunctionIds: selected };
    });
  };

  const save = async () => {
    if (!draft) return;
    const delay = draft.delayMinutes.trim();
    const request: UpsertFollowUpAutomationRequest = {
      name: draft.name.trim(),
      isEnabled: draft.isEnabled,
      assignedUseCaseIds: draft.assignedUseCaseIds,
      triggerKind: 'QuietFor',
      delayMinutes: delay === '' ? null : Number(delay),
      systemPrompt: draft.systemPrompt.trim(),
      allowedFunctionIds: draft.limitTools ? draft.allowedFunctionIds : [],
    };
    setSaving(true);
    setMessage(null);
    try {
      const saved = draft.id
        ? await updateFollowUpAutomation(draft.id, request)
        : await createFollowUpAutomation(request);
      setOperations((current) => sortOperations([
        ...current.filter((item) => item.id !== saved.id),
        saved,
      ]));
      setDraft(null);
      setMessageTone('success');
      setMessage(key('saved'));
    } catch (error) {
      setMessageTone('error');
      setMessage(error instanceof Error && error.message ? error.message : key('failed'));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm(key('deleteConfirm'))) return;
    setSaving(true);
    setMessage(null);
    try {
      await deleteFollowUpAutomation(id);
      setOperations((current) => current.filter((item) => item.id !== id));
      if (draft?.id === id) setDraft(null);
      setMessageTone('success');
      setMessage(key('deleted'));
    } catch (error) {
      setMessageTone('error');
      setMessage(error instanceof Error && error.message ? error.message : key('failed'));
    } finally {
      setSaving(false);
    }
  };

  const startCustomerReview = () => {
    if (!starterReady) {
      setMessageTone('error');
      setMessage(key('starterMissing'));
      return;
    }
    setMessage(null);
    setDraft({
      ...emptyDraft(),
      name: key('customerReviewName'),
      systemPrompt: starterPrompt,
    });
  };

  const surfaceName = (id: string) => {
    const surface = SURFACES.find((item) => item.id === id);
    return surface ? key(surface.labelKey) : id;
  };

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <div>
          <h3 className={styles.title}>{key('operationsTitle')}</h3>
          <p className={styles.intro}>{key('operationsHelp')}</p>
          <Link className={styles.toolLink} to={`/ai/config?tab=${encodeURIComponent(AiPromptUseCase.FollowUp)}`}>
            {key('toolsLinkCta')}
          </Link>
        </div>
        {canManage && !draft ? (
          <div className={styles.heroActions}>
            <button type="button" className="btn btn-primary" onClick={startCustomerReview}>
              {key('createCustomerReview')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setDraft(emptyDraft())}>
              {key('newOperation')}
            </button>
          </div>
        ) : null}
      </section>

      {message ? <p className={messageTone === 'error' ? styles.bannerError : styles.bannerSuccess}>{message}</p> : null}

      {loading ? <p className={styles.hint}>{key('loading')}</p> : operations.length === 0 ? (
        <p className={styles.empty}>{key('empty')}</p>
      ) : (
        <ul className={styles.list}>
          {operations.map((operation) => {
            const visibleSurfaces = operation.assignedUseCaseIds.slice(0, 4);
            const hiddenSurfaceCount = operation.assignedUseCaseIds.length - visibleSurfaces.length;
            return (
              <li key={operation.id} className={styles.row}>
                <button
                  type="button"
                  className={styles.rowMain}
                  disabled={!canManage}
                  onClick={() => setDraft(draftFrom(operation))}
                >
                  <span className={styles.rowName}>{operation.name}</span>
                  <span className={styles.rowChips}>
                    {visibleSurfaces.map((id) => (
                      <span key={id} className={styles.chip}>{surfaceName(id)}</span>
                    ))}
                    {hiddenSurfaceCount > 0 ? <span className={styles.chipMore}>+{hiddenSurfaceCount}</span> : null}
                    {operation.delayMinutes != null ? (
                      <span className={styles.chipQuiet}>
                        {t('aiSettings.useCases.followUp.delayChip', { count: operation.delayMinutes })}
                      </span>
                    ) : null}
                    {operation.assignedUseCaseIds.length === 0 ? (
                      <span className={styles.rowMeta}>{key('surfaces')}</span>
                    ) : null}
                  </span>
                </button>
                <span className={operation.isEnabled ? styles.pillOn : styles.pillOff}>
                  {operation.isEnabled ? key('enabled') : key('disabled')}
                </span>
                {canManage ? (
                  <button type="button" className={styles.deleteButton} disabled={saving} onClick={() => void remove(operation.id)}>
                    {key('delete')}
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}

      {draft ? (
        <section className={styles.editor}>
          <header className={styles.editorHeader}>
            <h4>{draft.id ? key('editOperation') : key('newOperation')}</h4>
          </header>

          <div className={styles.sectionCard}>
            <div className={styles.formGrid}>
              <label className={styles.field}>
                {key('name')}
                <input
                  className="text-input"
                  value={draft.name}
                  disabled={!canManage || saving}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                />
              </label>
              <label className={`${styles.field} ${styles.delayField}`}>
                {key('delayLabel')}
                <input
                  className="text-input"
                  type="number"
                  min={5}
                  max={10080}
                  step={5}
                  value={draft.delayMinutes}
                  disabled={!canManage || saving}
                  onChange={(event) => setDraft({ ...draft, delayMinutes: event.target.value })}
                />
                <span className={styles.hint}>{key('delayHelp')}</span>
              </label>
            </div>
            <label className={`${styles.switchRow} ${draft.isEnabled ? styles.switchRowOn : ''}`}>
              <span className={styles.switchTitle}>{key('enabled')}</span>
              <span className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={draft.isEnabled}
                  disabled={!canManage || saving}
                  onChange={(event) => setDraft({ ...draft, isEnabled: event.target.checked })}
                />
                <span className={styles.toggleSlider} />
              </span>
            </label>
          </div>

          <div className={styles.sectionCard}>
            <div className={styles.sectionHead}>
              <h5>{key('surfaces')}</h5>
              <span className={styles.countBadge}>
                {t('aiSettings.useCases.followUp.selectedCount', { count: draft.assignedUseCaseIds.length })}
              </span>
            </div>
            <p className={styles.hint}>{key('surfacesHelp')}</p>
            {SURFACE_GROUPS.map((group) => (
              <div key={group.titleKey} className={styles.choiceGroup}>
                <div className={styles.choiceTitle}>{key(group.titleKey)}</div>
                <div className={styles.choiceGrid}>
                  {group.items.map((surface) => (
                    <Choice
                      key={surface.id}
                      tone="channel"
                      label={key(surface.labelKey)}
                      checked={draft.assignedUseCaseIds.includes(surface.id)}
                      disabled={!canManage || saving}
                      onChange={() => toggleSurface(surface.id)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className={styles.promptEditor}>
            <div className={styles.promptEditorHeader}>
              <div className={styles.promptEditorHeaderContent}>
                <span className={styles.promptEditorBadge}>{key('prompt')}</span>
                <p className={styles.promptEditorHint}>{key('editorHelp')}</p>
              </div>
            </div>
            <div className={styles.promptEditorBody}>
              <textarea
                className={styles.promptTextarea}
                rows={16}
                value={draft.systemPrompt}
                disabled={!canManage || saving}
                spellCheck={false}
                onChange={(event) => setDraft({ ...draft, systemPrompt: event.target.value })}
              />
            </div>
            <div className={styles.promptEditorFooter}>
              <span>{t('aiSettings.editorFooterHint')}</span>
              <span>{t('aiSettings.characterCount', { count: draft.systemPrompt.length })}</span>
            </div>
          </div>

          <div className={styles.sectionCard}>
            <label className={`${styles.switchRow} ${draft.limitTools ? styles.switchRowTools : ''}`}>
              <span className={styles.switchCopy}>
                <span className={styles.switchTitle}>{key('limitTools')}</span>
                <span className={styles.hint}>{key('limitToolsHelp')}</span>
              </span>
              <span className={styles.toggle}>
                <input
                  type="checkbox"
                  checked={draft.limitTools}
                  disabled={!canManage || saving}
                  onChange={(event) => setDraft({ ...draft, limitTools: event.target.checked })}
                />
                <span className={styles.toggleSlider} />
              </span>
            </label>
            {draft.limitTools ? (
              enabledTools.length === 0 ? <p className={styles.hint}>{key('noTools')}</p> : (
                <>
                  <div className={styles.toolCountRow}>
                    <span className={styles.countBadge}>
                      {t('aiSettings.useCases.followUp.selectedCount', { count: draft.allowedFunctionIds.length })}
                    </span>
                  </div>
                  <div className={styles.toolGrid}>
                    {enabledTools.map((tool) => (
                      <Choice
                        key={tool.functionId}
                        tone="tool"
                        label={tool.functionDisplayName}
                        checked={draft.allowedFunctionIds.includes(tool.functionId)}
                        disabled={!canManage || saving}
                        onChange={() => toggleTool(tool.functionId)}
                      />
                    ))}
                  </div>
                </>
              )
            ) : null}
          </div>

          <div className={styles.actions}>
            <button type="button" className="btn btn-primary" disabled={!canManage || saving} onClick={() => void save()}>
              {key('save')}
            </button>
            <button type="button" className="btn btn-secondary" disabled={saving} onClick={() => setDraft(null)}>
              {key('cancel')}
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
};
